<div
  class="ci-multiselect"
  [class.ci-multiselect--invalid]="_invalid()"
  [class.ci-multiselect--open]="open()"
  [class.ci-multiselect--summary]="summary"
  (keydown)="onKeydown($event)"
>
  @if (label && !summary) {
    <label class="ci-multiselect__field-label" [attr.for]="id + '-search'">
      {{ label }}
      @if (required) {
        <span class="ci-multiselect__label-required" aria-hidden="true">*</span>
      }
    </label>
  }

  <div
    #field
    cdkOverlayOrigin
    #fieldOrigin="cdkOverlayOrigin"
    class="ci-multiselect__field"
    [class.ci-multiselect__field--open]="open()"
    [class.ci-multiselect__field--filled]="variant === 'filled'"
    [class.ci-multiselect__field--summary]="summary"
    [attr.role]="'combobox'"
    [attr.aria-expanded]="open()"
    [attr.aria-multiselectable]="true"
    [attr.aria-haspopup]="'listbox'"
    [attr.aria-controls]="id + '-listbox'"
    [attr.aria-activedescendant]="
      open() && focusedIndex() >= 0 ? optionId(focusedIndex()) : null
    "
    [attr.aria-label]="ariaLabel || null"
    [attr.aria-required]="required || null"
    [attr.aria-invalid]="_invalid()"
    [attr.aria-disabled]="_disabled()"
    [attr.aria-describedby]="_invalid() && errorText ? errorId : null"
    [attr.tabindex]="summary ? (_disabled() ? -1 : 0) : null"
    (mousedown)="onFieldClick($event)"
  >
    @if (label && summary) {
      <label class="ci-multiselect__field-label ci-multiselect__field-label--floating">
        {{ label }}
        @if (required) {
          <span class="ci-multiselect__label-required" aria-hidden="true">*</span>
        }
      </label>
    }

    @if (summary) {
      @if (value().length > 0) {
        <span class="ci-multiselect__summary">{{ summaryText() }}</span>
      } @else if (placeholder) {
        <span class="ci-multiselect__placeholder">{{ placeholder }}</span>
      }
    } @else {
      @for (chip of value(); track chip) {
        <span class="ci-multiselect__chip">
          <span>{{ displayWith(chip) }}</span>
          <button
            type="button"
            class="ci-multiselect__chip-remove"
            [attr.aria-label]="removeChipLabel(chip)"
            [disabled]="_disabled()"
            (mousedown)="$event.stopPropagation()"
            (click)="removeAt($index, $event)"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
              <path
                d="M1 1l8 8M9 1L1 9"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </span>
      }

      <input
        #search
        class="ci-multiselect__search"
        type="text"
        autocomplete="off"
        [id]="id + '-search'"
        [attr.placeholder]="value().length === 0 ? placeholder : searchPlaceholder"
        [disabled]="_disabled()"
        [ngModel]="searchTerm()"
        (ngModelChange)="onSearchInput($event)"
      />
    }

    <span class="ci-multiselect__chevron" aria-hidden="true">
      <svg width="12" height="12" viewBox="0 0 12 12">
        <path
          d="M2 4l4 4 4-4"
          stroke="currentColor"
          stroke-width="1.5"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
  </div>

  <ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="fieldOrigin"
    [cdkConnectedOverlayOpen]="open()"
    [cdkConnectedOverlayPositions]="panelPositions"
    [cdkConnectedOverlayWidth]="panelWidth()"
    [cdkConnectedOverlayScrollStrategy]="scrollStrategy"
    [cdkConnectedOverlayFlexibleDimensions]="true"
    [cdkConnectedOverlayPush]="true"
    (attach)="onOverlayAttached()"
    (overlayOutsideClick)="onOutsideClick($event)"
    (detach)="onDetach()"
  >
    <div
      class="ci-multiselect__panel"
      [class.ci-multiselect__panel--summary]="summary"
      (keydown)="onKeydown($event)"
    >
      <div class="ci-multiselect__panel-header">
        @if (summary) {
          <input
            class="ci-multiselect__panel-search"
            type="text"
            autocomplete="off"
            [attr.placeholder]="searchPlaceholder || placeholder"
            [ngModel]="searchTerm()"
            (ngModelChange)="onSearchInput($event)"
          />
        } @else {
          <span class="ci-multiselect__count" aria-live="polite">
            {{ countText() }}
          </span>
        }
        @if (value().length > 0) {
          <button
            type="button"
            class="ci-multiselect__clear"
            (mousedown)="$event.preventDefault()"
            (click)="clearAll($event)"
          >
            {{ clearLabel }}
          </button>
        }
      </div>

      <ul
        class="ci-multiselect__list"
        role="listbox"
        [attr.id]="id + '-listbox'"
        [attr.aria-multiselectable]="true"
      >
        @for (item of filtered(); track item; let i = $index) {
          @let selected = isSelected(item);
          @let disabled = isDisabled(item);
          <li>
            <button
              type="button"
              class="ci-multiselect__option"
              [class.ci-multiselect__option--selected]="selected"
              [class.ci-multiselect__option--focused]="focusedIndex() === i"
              [class.ci-multiselect__option--disabled]="disabled"
              [attr.id]="optionId(i)"
              [attr.role]="'option'"
              [attr.aria-selected]="selected"
              [attr.aria-disabled]="disabled"
              [disabled]="disabled"
              (mousedown)="$event.preventDefault()"
              (click)="toggle(item)"
            >
              <span class="ci-multiselect__box" aria-hidden="true"></span>
              <span class="ci-multiselect__label">{{ displayWith(item) }}</span>
            </button>
          </li>
        }
        @if (filtered().length === 0) {
          <li class="ci-multiselect__empty">{{ emptyLabel }}</li>
        }
      </ul>
    </div>
  </ng-template>

  @if (_invalid() && errorText) {
    <p class="ci-multiselect__error" role="alert" [id]="errorId">{{ errorText }}</p>
  }
</div>
